import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX, ReactNode } from "react";

/**
 * The frosted card the sign-in screens are built on — the customer support
 * portal's auth card, rebuilt in MUI (see `pages/LoginPage.tsx` for the full
 * note on what was ported and what deliberately differs).
 *
 * Extracted when the invitation-accept screen arrived so both screens share
 * ONE definition of the surface. A second hand-copied card is a second thing
 * to remember when the portal is re-skinned, and the two would drift apart on
 * the first re-skin that missed one.
 *
 * The footer note lives INSIDE the card on purpose: floated out under the page
 * it reads as a caption for the whole layout rather than as the last word of
 * the form.
 */
export function AuthCard({
  title,
  description,
  footer,
  children,
}: {
  title: string;
  description: ReactNode;
  /** The hairline-ruled quiet line at the bottom. Omitted renders no rule. */
  footer?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        mx: "auto",
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
        backdropFilter: "blur(14px)",
        boxShadow: (t) =>
          t.palette.mode === "dark"
            ? `0 24px 60px -12px ${alpha(t.palette.common.black, 0.6)}`
            : `0 1px 2px ${alpha(t.palette.common.black, 0.04)}, 0 24px 60px -30px ${alpha(
                t.palette.common.black,
                0.22,
              )}`,
        px: { xs: 3, sm: 4.5 },
        py: { xs: 4, sm: 5 },
      }}
    >
      {/* A plain heading, not a nested <header>: a nested <header> also maps
          to the `banner` landmark, which would give the page two — the white
          top bar is the only banner here. */}
      <Typography
        variant="h2"
        component="h2"
        sx={{
          fontSize: { xs: "1.6rem", sm: "1.75rem" },
          fontWeight: 600,
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </Typography>
      <Typography
        component="p"
        sx={{ mt: 0.75, fontSize: 13.5, lineHeight: 1.6, color: "text.secondary" }}
      >
        {description}
      </Typography>

      {children}

      {footer !== undefined && (
        <Box
          sx={{
            mt: 3.5,
            pt: 2.5,
            borderTop: 1,
            borderColor: "divider",
            fontSize: 12,
            lineHeight: 1.6,
            color: "text.secondary",
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}

/**
 * The card's one primary action. Teal gradient, full width, 44px — the same
 * button on every auth screen.
 */
export function SubmitButton({
  children,
  disabled,
}: {
  children: ReactNode;
  disabled?: boolean;
}): JSX.Element {
  return (
    <Box
      component="button"
      type="submit"
      disabled={disabled ?? false}
      sx={{
        mt: 0.5,
        width: "100%",
        height: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        border: 0,
        borderRadius: 1.5,
        cursor: "pointer",
        font: "inherit",
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: "-0.01em",
        color: "accent.contrastText",
        backgroundImage: (t) =>
          `linear-gradient(110deg, ${t.palette.accent[600]} 0%, ${t.palette.accent[700]} 55%, ${t.palette.accent[800]} 100%)`,
        boxShadow: (t) => `0 10px 28px -10px ${alpha(t.palette.accent[700], 0.75)}`,
        transition: "filter 200ms, box-shadow 200ms",
        "&:hover:not(:disabled)": {
          filter: "brightness(1.12)",
          boxShadow: (t) => `0 14px 32px -8px ${alpha(t.palette.accent[600], 0.75)}`,
        },
        "&:focus-visible": {
          outline: (t) => `2px solid ${t.palette.accent[500]}`,
          outlineOffset: 2,
        },
        "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * The card's inline message strip.
 *
 * `tone` picks the ARIA wiring, not the colour: a failure is `alert` +
 * `assertive` because the person is mid-task and needs it now, while a
 * confirmation is `status` + `polite` so a screen reader finishes the
 * sentence it is on first. The surface is the same accent-soft strip either
 * way — the portal has one notice style.
 */
export function AuthNotice({
  tone = "error",
  children,
}: {
  tone?: "error" | "info";
  children: ReactNode;
}): JSX.Element {
  return (
    <Box
      role={tone === "error" ? "alert" : "status"}
      aria-live={tone === "error" ? "assertive" : "polite"}
      sx={{
        borderRadius: 1.5,
        border: 1,
        borderColor: (t) => alpha(t.palette.accent[500], 0.3),
        bgcolor: (t) => t.palette.accent.soft,
        px: 1.75,
        py: 1.25,
        fontSize: 12.5,
        lineHeight: 1.55,
        fontWeight: 500,
        color: "text.primary",
      }}
    >
      {children}
    </Box>
  );
}
